import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMantineTheme, useMediaQuery } from 'hooks'
import { Modal, Text, Checkbox, Group, Button, Stack } from 'components'
import { IconAlertTriangle } from '@tabler/icons-react'

interface RegenerateMeetingsConfirmModalProps {
  opened: boolean
  onClose: () => void
  onConfirm: () => void
  meetingsCount: number
}

export function RegenerateMeetingsConfirmModal({
  opened,
  onClose,
  onConfirm,
  meetingsCount,
}: RegenerateMeetingsConfirmModalProps) {
  const { t } = useTranslation()
  const theme = useMantineTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)
  const [checked, setChecked] = useState(false)
  const [confirmDisabled, setConfirmDisabled] = useState(true)

  const handleConfirm = () => {
    if (checked) {
      onConfirm()
      setChecked(false)
      setConfirmDisabled(true)
    }
  }

  const handleClose = () => {
    onClose()
    setChecked(false)
    setConfirmDisabled(true)
  }

  useEffect(() => {
    setConfirmDisabled(!checked)
  }, [checked])

  return (
    <Modal
      fullScreen={isMobile}
      size='md'
      opened={opened}
      onClose={handleClose}
      title={t('new_project_page.regenerate_meetings_title')}
      centered
      zIndex={300}
    >
      <Stack spacing='md'>
        <Group spacing='sm' align='flex-start'>
          <IconAlertTriangle size={24} color={theme.colors.yellow[6]} />
          <Text size='md'>
            {t('new_project_page.regenerate_meetings_description', {
              count: meetingsCount,
            })}
          </Text>
        </Group>

        <Checkbox
          size='md'
          label={t('new_project_page.regenerate_meetings_checkbox')}
          checked={checked}
          onChange={event => setChecked(event.currentTarget.checked)}
        />

        <Group spacing='sm' position='right' mt='md'>
          <Button variant='default' size='lg' onClick={handleClose}>
            {t('general_buttons_cancel')}
          </Button>
          <Button
            variant='filled'
            color='grape'
            size='lg'
            onClick={handleConfirm}
            disabled={confirmDisabled}
          >
            {t('new_project_page.regenerate_and_continue')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
